import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import { DataGrid } from '@mui/x-data-grid';
import useApiManager from '../../../utils/Api/apiManager';
import { useNavigate, Link } from "react-router-dom";
import { format } from 'date-fns';
import { CampaignStatus, CampaignType } from '../../../utils/enums';

const CampaignsDataGrid = ({ filters, selectedRows, onSelectionModelChange, contactId, triggerFetch, setTriggerFetch }) => {
    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [sortModel, setSortModel] = useState([]);
    const navigate = useNavigate();
    const { post } = useApiManager();
    const [paginationModel, setPaginationModel] = React.useState({
        page: 0,
        pageSize: 50,
    });
    const [filterModel, setFilterModel] = useState({ items: [] });
    const [filterParams, setFilterParams] = useState([]);

    const columns = [
        { field: 'id', headerName: 'ID', flex: 0.5 },
        {
            field: 'campaignName',
            headerName: 'Campaign Name',
            flex: 1,
            renderCell: (params) => {
                const displayName = params.value?.trim() || "(N/A)"; // fallback if null, undefined, or empty
                return (
                    <Link
                        to={`/campaigns/${params.row.id}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: 'inherit' }}
                    >
                        {displayName}
                    </Link>
                );
            },
        },
        { field: 'type', headerName: 'Type', flex: 1 },
        { field: 'status', headerName: 'Status', flex: 1 },
        { field: 'startDate', headerName: 'Start Date', flex: 1 },
        { field: 'endDate', headerName: 'End Date', flex: 1 },
        { field: 'numberOfLeads', headerName: 'Leads Count', flex: 1 },
        { field: 'numberOfContacts', headerName: 'Contacts Count', flex: 1 },
        { field: 'numberOfDeals', headerName: 'Deals Count', flex: 1 },
        { field: 'campaignOwner', headerName: 'Campaign Owner', flex: 1 },
    ];

    const transformData = (data) => {
        return data.map(item => ({
            id: item.id,
            campaignName: item.campaignName,
            type: CampaignType[item.type].label,
            status: CampaignStatus[item.status].label,
            startDate: item.startDate ? format(item.startDate, 'MMMM do yyyy') : '',
            endDate: item.endDate ? format(item.endDate, 'MMMM do yyyy') : '',
            numberOfLeads: item.numberOfLeads ?? 0,
            numberOfContacts: item.numberOfContacts ?? 0,
            numberOfDeals: item.numberOfDeals ?? 0,
            campaignOwner: item.ownerName
        }));
    };

    useEffect(() => {
        setFilterParams(filters);
    }, [filters]);

    useEffect(() => {
        const fetchAndSetData = async () => {
            setLoading(true);
            try {


                const sortField = sortModel.length > 0 ? sortModel[0].field : 'createdAt';
                const sortDescending = sortModel.length > 0 ? sortModel[0].sort === 'desc' : true;

                let url = `/campaigns/paged`;
                // gets campaigns that do not have this contactId
                if (contactId) {
                    url += `/${contactId}`;
                }

                const data = await post(url, {
                    pageNumber: paginationModel.page + 1,
                    pageSize: paginationModel.pageSize,
                    filters: filterParams,
                    sortField: sortField,
                    sortDescending: sortDescending
                });
                setRows(transformData(data.items));
                setRowCount(data.totalCount);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
                if (setTriggerFetch) {
                    setTriggerFetch(false);
                }
            }
        };

        fetchAndSetData();
    }, [paginationModel, filterParams, sortModel, triggerFetch]);

    const handleRowClick = (params) => {
        navigate(`/campaigns/${params.id}`);
    };

    return (
        <DataGrid
            rows={rows}
            columns={columns}
            rowCount={rowCount}
            paginationMode="server"
            sortingMode="server"
            columnVisibilityModel={{
                id: false,
            }}
            getRowId={(row) => row.id}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[50, 75, 100]}
            onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
            filterMode="server"
            onFilterModelChange={(newFilterModel) => {
                setFilterModel(newFilterModel);
            }}
            checkboxSelection
            rowSelectionModel={selectedRows}
            onRowSelectionModelChange={onSelectionModelChange}
            disableRowSelectionOnClick
            loading={loading}
            onRowClick={handleRowClick}
            sx={{
                height: '100%', // Fills the parent container
                '& .MuiDataGrid-virtualScroller': {
                    overflow: 'auto', // Ensures scrollbars appear when necessary
                },
            }}
        />
    );
};

export default CampaignsDataGrid;
